import metadataServices from '@sstdev/lib_metadata-services';
import combineMetadata from '../../metadata/utilities/combineMetadata';
import { uiRuntimeConfig, constants } from 'lib_ui-services';

const { dataDictionaryService } = metadataServices;
const _p = {
    combineMetadata,
    calculateDataDictionary: dataDictionaryService.transform
};
export const _private = _p;

export default {
    verb: 'doingCreate',
    namespace: 'metaui',
    relation: 'dataDictionary',
    description: '',
    //type: '',
    //priority:0,
    //useCaseIds:[]
    prerequisites: [
        //version detail (different from the others as we need to match on _id, rather than on metadata:useCaseDetail._id )
        {
            context: {
                verb: 'get',
                namespace: 'metadata',
                relation: 'useCaseDetail',
                type: 'find'
            },
            query: ({ data }) => {
                const {
                    'metadata:useCaseDetail': { _id }
                } = data;
                return {
                    _id,
                    'meta.deleted': { $exists: false }
                };
            }
        },
        //namespaces
        getLinkedDocumentsFor('namespace'),
        //profile menu
        getLinkedDocumentsFor('profileMenu'),
        //navigation
        getLinkedDocumentsFor('navigation'),
        //pages
        getLinkedDocumentsFor('page')
    ],
    //this is the actual logic:
    logic: doingCreate
};
function getLinkedDocumentsFor(relation) {
    return {
        context: {
            verb: 'get',
            namespace: 'metadata',
            relation: relation,
            type: 'find'
        },
        query: ({ data }) => {
            const {
                'metadata:useCaseDetail': { _id }
            } = data;
            return {
                'metadata:useCaseDetail._id': _id,
                'meta.deleted': { $exists: false }
            };
        }
    };
}

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").Context} Context
 */

/**
 * @param {{
 *   data: T;
 *   prerequisiteResults: Array<{result:Array<object>}>;
 *   context: Context;
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 * @returns {T}
 */
async function doingCreate({
    data,
    data: {
        'metadata:page': submittedPage,
        'metadata:profileMenu': submittedProfileMenu,
        'identity:role': role = { title: 'DEITY' }
    },
    dispatch,
    prerequisiteResults,
    log = { error() {} }
}) {
    const [
        {
            result: [useCaseDetail]
        },
        { result: namespaces },
        {
            result: [profileMenu]
        },
        {
            result: [navigation]
        },
        { result: _pages }
    ] = prerequisiteResults;
    let pages;
    if (submittedPage) {
        pages = _pages.filter(x => x._id !== submittedPage._id).concat(submittedPage);
    } else {
        pages = _pages;
    }
    const combined = await _p.combineMetadata(
        namespaces,
        submittedProfileMenu || profileMenu,
        navigation,
        pages,
        useCaseDetail,
        dispatch
    );
    const errors = [];
    const notificationHandler = (message, blockId) => {
        errors.push({ message, blockId });
        dispatch(
            {
                message,
                blockId,
                timeout: uiRuntimeConfig.getNotificationTimeoutFormError(),
                addToList: true,
                isError: true, //todo: gl2025 remove
                variant: 'error',
                type: constants.notificationTypes.TOAST
            },
            { verb: 'pop', namespace: 'application', relation: 'notification' }
        );
    };
    let _accessRole = role.title;
    if (!Object.keys(constants.WELL_KNOWN_ROLES).includes(role.title)) {
        _accessRole = 'ADMIN';
    }
    const dataDictionary = _p.calculateDataDictionary(combined, _accessRole, notificationHandler);

    try {
        const featureFlags = extractFeatureFlagsFromMetadata(pages, navigation);
        const tenantSettings = extractTenantSettingsFromMetadata(pages, navigation);

        dataDictionary._featureFlags = featureFlags;
        dataDictionary._tenantSettings = tenantSettings;
    } catch (error) {
        log.error('[dataDictionary] Extraction error', error);
        // Don't fail the whole operation if extraction fails
        dataDictionary._featureFlags = [];
        dataDictionary._tenantSettings = [];
    }

    return { ...data, errors, result: dataDictionary };
}

/**
 * Extract feature flags from pages and navigation entries
 * @param {Array} pages - Array of page objects with blockly structure
 * @param {Object} navigation - Navigation object (may have children)
 * @returns {Array} Array of feature flag objects with title and locations
 */
function extractFeatureFlagsFromMetadata(pages = [], navigation = null) {
    return extractFlagsFromMetadata(
        pages,
        navigation,
        ['accessModifier_AddForFeatureFlag', 'accessModifier_RemoveForFeatureFlag'],
        'featureFlag'
    );
}

/**
 * Extract tenant settings from pages and navigation entries
 * @param {Array} pages - Array of page objects with blockly structure
 * @param {Object} navigation - Navigation object (may have children)
 * @returns {Object} Map of tenant setting names to location arrays
 */
function extractTenantSettingsFromMetadata(pages = [], navigation = null) {
    return extractFlagsFromMetadata(
        pages,
        navigation,
        ['accessModifier_ShowForTenantSetting', 'accessModifier_HideForTenantSetting'],
        'tenantSetting'
    );
}

/**
 * Generic function to extract flags from pages and navigation entries
 * @param {Array} pages - Array of page objects with blockly structure
 * @param {Object} navigation - Navigation object (may have children)
 * @param {Array<string>} blockTypes - Block type names to look for
 * @param {string} fieldName - Field name to extract from matching blocks
 * @returns {Object} Map of flag names to location arrays
 */
function extractFlagsFromMetadata(pages, navigation, blockTypes, fieldName) {
    const flags = {};

    // Extract from pages - walk through blockly structure
    if (Array.isArray(pages)) {
        pages.forEach(page => {
            if (page.blockly?.blocks?.blocks) {
                const pageFlags = extractFlagsFromBlocklyBlocks(page.blockly.blocks.blocks, blockTypes, fieldName);
                pageFlags.forEach(flagName => {
                    if (!flags[flagName]) {
                        flags[flagName] = [];
                    }
                    flags[flagName].push(page.title);
                });
            }
        });
    }

    // Extract from navigation entries (they may have blockly structure too)
    if (navigation) {
        const navFlags = extractFlagsFromNavigationTree(navigation, blockTypes, fieldName);
        Object.entries(navFlags).forEach(([flagName, titles]) => {
            if (!flags[flagName]) {
                flags[flagName] = [];
            }
            flags[flagName].push(...titles);
        });
    }

    // Deduplicate locations for each extracted flag.
    return Object.entries(flags).reduce((result, [flagName, locations]) => {
        result[flagName] = [...new Set(locations)];
        return result;
    }, {});
}

/**
 * Extract flags from blockly blocks structure
 * @param {Array} blocks - Array of blockly block objects
 * @param {Array<string>} blockTypes - Block type names to look for
 * @param {string} fieldName - Field name to extract from matching blocks
 * @param {Set<string>} visited - Set of visited block IDs to prevent infinite recursion
 * @returns {Array<string>} Array of flag/setting names found
 */
function extractFlagsFromBlocklyBlocks(
    blocks,
    blockTypes,
    fieldName,
    visited = new Set(),
    visitedBlocks = new WeakSet()
) {
    const flags = [];

    if (!Array.isArray(blocks)) {
        blocks = [blocks];
    }

    blocks.forEach(block => {
        if (!block || typeof block !== 'object') return;

        // Prevent cycles when blocks do not have IDs by tracking object references.
        if (visitedBlocks.has(block)) return;
        visitedBlocks.add(block);

        // Prevent infinite recursion by tracking visited blocks
        if (block.id && visited.has(block.id)) return;
        if (block.id) visited.add(block.id);

        // Check if this block matches one of our target types
        if (blockTypes.includes(block.type) && block.fields?.[fieldName]) {
            flags.push(block.fields[fieldName]);
        }

        // Recursively check nested blocks in inputs
        if (block.inputs) {
            Object.values(block.inputs).forEach(input => {
                if (input.block) {
                    flags.push(
                        ...extractFlagsFromBlocklyBlocks([input.block], blockTypes, fieldName, visited, visitedBlocks)
                    );
                }
                if (input.shadow) {
                    flags.push(
                        ...extractFlagsFromBlocklyBlocks([input.shadow], blockTypes, fieldName, visited, visitedBlocks)
                    );
                }
            });
        }

        // Recursively check next blocks
        if (block.next) {
            if (block.next.block) {
                flags.push(
                    ...extractFlagsFromBlocklyBlocks([block.next.block], blockTypes, fieldName, visited, visitedBlocks)
                );
            }
            if (block.next.shadow) {
                flags.push(
                    ...extractFlagsFromBlocklyBlocks([block.next.shadow], blockTypes, fieldName, visited, visitedBlocks)
                );
            }
        }
    });

    return flags;
}

/**
 * Extract flags from navigation tree recursively
 * @param {Object} navNode - Navigation node
 * @param {Array<string>} blockTypes - Block type names to look for
 * @param {string} fieldName - Field name to extract from matching blocks
 * @returns {Object} Map of flag names to array of navigation titles
 */
function extractFlagsFromNavigationTree(navNode, blockTypes, fieldName) {
    const flags = {};

    if (!navNode) return flags;

    // Check if navigation node has blockly structure
    if (navNode.blockly?.blocks?.blocks) {
        const navFlags = extractFlagsFromBlocklyBlocks(navNode.blockly.blocks.blocks, blockTypes, fieldName);
        navFlags.forEach(flagName => {
            const locationLabel = navNode.title || navNode.id || '(untitled)';
            if (!flags[flagName]) {
                flags[flagName] = [];
            }
            flags[flagName].push(locationLabel);
        });
    }

    // Recursively extract from children
    if (Array.isArray(navNode.children)) {
        navNode.children.forEach(child => {
            const childFlags = extractFlagsFromNavigationTree(child, blockTypes, fieldName);
            Object.entries(childFlags).forEach(([flagName, titles]) => {
                if (!flags[flagName]) {
                    flags[flagName] = [];
                }
                flags[flagName].push(...titles);
            });
        });
    }

    return flags;
}

Object.assign(_p, {
    extractFeatureFlagsFromMetadata,
    extractTenantSettingsFromMetadata,
    extractFlagsFromMetadata,
    extractFlagsFromBlocklyBlocks,
    extractFlagsFromNavigationTree
});
